// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { selectBasketIsCashout } from '../../selectors/basket/basketPrimary.selector';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import {
  makeIsAnyOfBasketMatchItemsCashoutingSelector,
  selectMatchViewMode,
  selectMatchTMM,
} from '../../selectors/match.selector';
import { BasketStatuses } from '../../types/basket';
import { MatchViewModes } from '../../types/matchView';
import { type TopMyMatch } from '../../types/topMyMatch';

const multichanceOutcomeSortingFn = (a, b) => a - b;
const isOutcomeMultichance = (outcome) =>
  typeof outcome === 'object' && outcome.or && Array.isArray(outcome.or) && !!outcome.or.length;

const makeMatchTopMyMatchCardPropsSelector = () => {
  const emptyArray: [] = [];
  const emptyObject = {};

  const getMatchIdFromProps = (_, { matchId }) => matchId;
  const getIndexFromProps = (_, { index }) => index;

  const selectCardData = createSelector([getIndexFromProps, selectMatchTMM], (index, matchTMM) => matchTMM[index]);

  const selectCardDataOutcomes = createSelector(
    [selectCardData],
    (cardData: TopMyMatch) => cardData?.outcomes ?? emptyArray,
  );

  const selectCardDataOdds = createSelector([selectCardData], (cardData: TopMyMatch) => cardData?.odds ?? emptyArray);

  const selectOutcomesBetTitles = createSelector(
    [selectCardDataOutcomes, selectEntitiesBets],
    (outcomesFromProps, bets) => {
      const res = {};
      const betsAsArray = Object.keys(bets).map((betId) => bets[betId]);

      betsAsArray.forEach((bet) => {
        const betOutcomes = bet?.outcomes;
        if (betOutcomes?.length) {
          for (const outcomeId of betOutcomes) {
            if (~(outcomesFromProps?.indexOf(outcomeId) ?? -1)) {
              res[outcomeId] = bet.betTitle;
            }
          }
        }
      }, {});

      const multichancesOutcomes =
        outcomesFromProps?.filter((outcomeFromProps) => isOutcomeMultichance(outcomeFromProps)) ?? [];

      if (multichancesOutcomes.length) {
        multichancesOutcomes.forEach(({ or: outcomeIds }) => {
          const id = outcomeIds.sort(multichanceOutcomeSortingFn).join('-');
          const targetedBets = betsAsArray.filter(
            (bet) => !!bet?.outcomes?.find((betOutcomeId) => ~outcomeIds.indexOf(betOutcomeId)),
          );
          const betTitle = targetedBets[0]?.betTitle;
          res[id] = __('%1$s (multiple chance)', betTitle);
        });
      }

      return res;
    },
  );

  const selectOutcomesValues = createSelector(
    [selectCardDataOutcomes, selectEntitiesOutcomes, selectOutcomesBetTitles],
    (outcomesFromProps, outcomes, betTitlesByOutcome) =>
      outcomesFromProps?.reduce((map, outcomeFromProps) => {
        if (isOutcomeMultichance(outcomeFromProps)) {
          // multichance
          const outcomeIds = outcomeFromProps.or;
          const id = outcomeIds.sort(multichanceOutcomeSortingFn).join('-');
          const label = outcomeIds
            .reduce((acc, outcomeId) => (outcomes[outcomeId]?.label ? [...acc, outcomes[outcomeId]?.label] : acc), [])
            .join(` ${__('or')} `);
          const title = betTitlesByOutcome[id];

          return {
            ...map,
            [id]: {
              title,
              description: label,
            },
          };
        } else {
          const { label } = outcomes[outcomeFromProps] ?? {};

          if (label) {
            return {
              ...map,
              [outcomeFromProps]: {
                title: betTitlesByOutcome[outcomeFromProps],
                description: label,
              },
            };
          }
        }

        return map;
      }, {}) ?? emptyObject,
  );

  const selectOutcomeBetIds = createSelector(
    [selectCardDataOutcomes, selectEntitiesBets],
    (outcomesFromProps, bets) => {
      const res = {};
      const betsAsArray = Object.keys(bets).map((betId) => bets[betId]);

      betsAsArray.forEach((bet) => {
        const betOutcomes = bet?.outcomes;
        if (betOutcomes?.length) {
          for (const outcomeId of betOutcomes) {
            if (~(outcomesFromProps?.indexOf(outcomeId) ?? -1)) {
              res[outcomeId] = bet.betId;
            }
          }
        }
      }, {});

      const multichancesOutcomes =
        outcomesFromProps?.filter((outcomeFromProps) => isOutcomeMultichance(outcomeFromProps)) ?? [];

      if (multichancesOutcomes.length) {
        multichancesOutcomes.forEach(({ or: outcomeIds }) => {
          const id = outcomeIds.sort(multichanceOutcomeSortingFn).join('-');
          const targetedBets = betsAsArray.filter(
            (bet) => !!bet?.outcomes?.find((betOutcomeId) => ~outcomeIds.indexOf(betOutcomeId)),
          );
          const betId = targetedBets[0]?.betId;
          res[id] = betId;
        });
      }

      return res;
    },
  );

  const selectCardItems = createSelector(
    [selectCardDataOutcomes, selectOutcomesValues],
    (outcomesFromProps, outcomesValues) => {
      if (outcomesFromProps?.length) {
        return outcomesFromProps.reduce((acc, outcomeFromProps) => {
          if (isOutcomeMultichance(outcomeFromProps)) {
            const value = outcomesValues[outcomeFromProps.or.join('-')];

            return value ? [...acc, value] : acc;
          }

          return outcomesValues[outcomeFromProps] ? [...acc, outcomesValues[outcomeFromProps]] : acc;
        }, []);
      }
      return emptyArray;
    },
  );

  const selectMyMatchViewModeIsAvailable = createSelector(
    [selectMatchViewMode, getMatchIdFromProps],
    (matchViewMode, matchIdFromMatchView) => matchViewMode === MatchViewModes.MY_MATCH && !!matchIdFromMatchView,
  );

  const selectProcessedOutcomes = createSelector(
    [getMatchIdFromProps, selectCardDataOutcomes, selectOutcomeBetIds],
    (matchId, outcomesFromProps, betIdByOutcomes) =>
      outcomesFromProps?.map((outcomeFromProps) => {
        if (isOutcomeMultichance(outcomeFromProps)) {
          const sortedOutcomes = outcomeFromProps.or.sort(multichanceOutcomeSortingFn);
          const id = sortedOutcomes.join('-');
          return {
            matchId,
            outcomeIds: sortedOutcomes,
            betId: betIdByOutcomes[id],
            status: BasketStatuses.OK,
          };
        }
        return {
          matchId,
          outcomeId: outcomeFromProps,
          betId: betIdByOutcomes[outcomeFromProps],
          status: BasketStatuses.OK,
        };
      }) ?? emptyArray,
  );

  const selectIsDisabled = createSelector(
    [selectBasketIsCashout, makeIsAnyOfBasketMatchItemsCashoutingSelector()],
    (isCashout, isAnyOfBasketMatchItemsCashouting) => isCashout && isAnyOfBasketMatchItemsCashouting,
  );

  return createSelector(
    [selectCardItems, selectCardDataOdds, selectMyMatchViewModeIsAvailable, selectProcessedOutcomes, selectIsDisabled],
    (items, odds, myMatchViewModeIsAvailable, processedOutcomes, disabled) => ({
      items,
      odds,
      myMatchViewModeIsAvailable,
      processedOutcomes,
      disabled,
    }),
  );
};

export default makeMatchTopMyMatchCardPropsSelector;
